import React, {
  useEffect, useState, useCallback, useMemo, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useParams, Navigate } from 'react-router';
import TextHeader from '../../../../components/text-header/text-header.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import Grid from '../../../../components/grid/grid.component';
import GridItem from '../../../../components/grid-item/grid-item.component';
import LineBreak from '../../../../components/line-break/line-break.component';
import ErrorBlock from '../../../../components/error-block/error-block.component';
import Loader from '../../../../components/loader/loader.component';
import FilteredLink from '../../../../components/filtered-link/filtered-link.component';
import ActionBlock from '../../../../components/action-block/action-block.component';
import StatusBar from '../../../../components/status-bar/status-bar.component';
import PageError from '../../../../components/page-error/page-error.component';
import Button from '../../../../components/button/button.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import Table from '../../../../components/table/table.component';
import TableHead from '../../../../components/table-head/table-head.component';
import TableBody from '../../../../components/table-body/table-body.component';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import PaddedFlexContainer from '../../../../components/padded-flex-container/padded-flex-container.component';
import ConfirmModal from '../../../../components/confirm-modal/confirm-modal.component';
import ConnectOtpModal from '../../../../components/connect-otp-modal/connect-otp-modal.component';
import VerifyPhoneModal from '../../../../components/verify-phone-modal/verify-phone-modal.component';
import Form from '../../../../components/form/form.component';
import FormContainer from '../../../../components/form-container/form-container.component';
import FormFieldPhone from '../../../../components/form-field-phone/form-field-phone.component';
import FormFieldSelect from '../../../../components/form-field-select/form-field-select.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import VerificationSetupValidator from './verification-setup.validator';
import FormatService from '../../../../services/format-service';
import FormService from '../../../../services/form-service';
import useScreenSmall from '../../../../hooks/use-screen-small';
import useScreenMobile from '../../../../hooks/use-screen-mobile';
import useServerFormErrors from '../../../../hooks/use-server-form-errors';
import {
  getUserVerificationSetupData, sendPhoneVerificationCodeSetup, verifyUserPhoneSetup, generateOtpSecretDataSetup,
  verifyUserOtpSetup, deleteOtpSecretDataSetup, saveUserVerificationData,
} from './verification-setup.action';
import { resetFormErrors } from '../../app.action';
import { FORM_ID_VERIFICATION_SETUP } from '../../../../constants/system/form-ids';
import { VERIFICATION_TYPE_AUTHENTICATOR_APP_OTP } from '../../../../constants/entity/verification-types';
import {
  ROUTE_DASHBOARD, ROUTE_LOGIN, ROUTE_NOT_FOUND, ROUTE_PARAM_HASH,
} from '../../../../constants/system/routes';

const VerificationSetup: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL: number = 2;

  const {
    isDataLoaded,
    currentUser,
    userVerificationSetupData,
    actionHash,
    otpSecretHash,
    otpSecretQrCodeLink,
    homepageRoute,
    form: formProps,
    handleSubmit: submitForm,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    change: changeForm,
  } = formProps;

  const isSmallScreen: boolean = useScreenSmall();
  const isMobileScreen: boolean = useScreenMobile();
  const routeParams: ReactRouterParams = useParams();
  const dispatch: ReduxDispatch = useDispatch();

  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [userPhone, setUserPhone]: [string | null, Function] = useState(null);
  const [isUserPhoneVerified, setIsUserPhoneVerified]: [boolean | null, Function] = useState(null);
  const [isVerifyPhoneModalOpen, setIsVerifyPhoneModalOpen]: [boolean, Function] = useState(false);
  const [isConnectOtpModalOpen, setIsConnectOtpModalOpen]: [boolean, Function] = useState(false);
  const [verifyPhoneModalErrorMessage, setVerifyPhoneModalErrorMessage]: [string | null, Function] = useState(null);
  const [connectOtpModalErrorMessage, setConnectOtpModalErrorMessage]: [string | null, Function] = useState(null);
  const [isDeleteOtpSecretConfirmModalOpen, setIsDeleteOtpSecretConfirmModalOpen]: [boolean, Function]
    = useState(false);

  const actionHashParam: string = routeParams[ROUTE_PARAM_HASH];
  const withServerErrors: boolean = !!serverFormErrors && !!Object.keys(serverFormErrors).length;
  const verificationTypeList: VerificationType[] = (userVerificationSetupData
    && userVerificationSetupData.verificationTypeList) || [];
  const userId: number | null = (userVerificationSetupData
    && userVerificationSetupData.user && userVerificationSetupData.user.id) || null;
  const userVerificationTypeDefault: string = (userVerificationSetupData
    && userVerificationSetupData.user && userVerificationSetupData.user.verificationTypeDefault) || '';
  const userIsPhoneVerified: boolean = (userVerificationSetupData
    && userVerificationSetupData.user && userVerificationSetupData.user.isPhoneVerified) || false;
  const userIsOtpEnabled: boolean = (userVerificationSetupData
    && userVerificationSetupData.user && userVerificationSetupData.user.isOtpEnabled) || false;
  const userIsOtpVerified: boolean = (userVerificationSetupData
    && userVerificationSetupData.user && userVerificationSetupData.user.isOtpVerified) || false;
  const isSaveDisabled: boolean = !isUserPhoneVerified && !userIsOtpVerified;
  const deleteOtpSecretConfirmModalTitle: string = 'Delete Authenticator Data';
  const deleteOtpSecretConfirmModalText: string = 'Are you sure you want to delete Authenticator App data? '
    + 'This MFA method won\'t be available until you connect it to the app again.';

  const menuItemVerificationTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: verificationTypeList,
  }), [verificationTypeList]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values || !userId || !actionHashParam) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveUserVerificationData({
        verificationForm: {
          phone: values.phone && FormatService.isValidPhoneNumber(values.phone, true)
            ? FormatService.formatPhoneValue(values.phone) : null,
          type: values.verificationTypeDefault,
          actionHash: actionHashParam,
        },
        userId,
      }, (response: ActionResponse): void => {
        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [userId, actionHashParam]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [formValues, withFormErrors, userId, actionHashParam]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const phoneChange: PhoneChangeHandlerFunction = useCallback((phoneValue?: string): void => {
    if (phoneValue !== userPhone) {
      setUserPhone(phoneValue ? FormatService.formatPhoneValue(phoneValue) : null);
      setIsUserPhoneVerified(false);
    }
  }, [userPhone]);

  const connectOtpButtonClick: Function = useCallback((): void => {
    if (!userId || !actionHashParam) {
      return;
    }

    setIsConnectOtpModalOpen(true);

    dispatch(generateOtpSecretDataSetup({
      actionHash: actionHashParam,
      userId,
    }, (): void => {
      setConnectOtpModalErrorMessage(null);
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setConnectOtpModalErrorMessage(exception.message);
      }
    }));
  }, [userId, actionHashParam]);

  const deleteOtpSecretButtonClick: Function = useCallback((): void => {
    setIsDeleteOtpSecretConfirmModalOpen(true);
  }, []);

  const closeDeleteOtpSecretConfirmModal: Function = useCallback((): void => {
    setIsDeleteOtpSecretConfirmModalOpen(false);
  }, []);

  const deleteOtpSecretConfirm: Function = useCallback((): void => {
    if (!userId || !actionHashParam) {
      return;
    }

    dispatch(deleteOtpSecretDataSetup({
      userId,
      actionHash: actionHashParam,
    }, (): void => {
      setStatusMessage('Authenticator data deleted successfully');
      setIsOpenMessage(true);

      if (userVerificationTypeDefault === VERIFICATION_TYPE_AUTHENTICATOR_APP_OTP) {
        changeForm('verificationTypeDefault', '');
      }

      closeDeleteOtpSecretConfirmModal();
    }));
  }, [userId, actionHashParam, userVerificationTypeDefault]);

  const verifyPhone: Function = useCallback((): void => {
    if (!userId || !actionHashParam || !userPhone || !FormatService.isValidPhoneNumber(userPhone)) {
      return;
    }

    setIsVerifyPhoneModalOpen(true);

    dispatch(sendPhoneVerificationCodeSetup({
      actionHash: actionHashParam,
      phone: userPhone,
      userId,
    }, undefined, (exception: ServerError): void => {
      if (exception && exception.message) {
        setVerifyPhoneModalErrorMessage(exception.message);
      }
    }));
  }, [userId, actionHashParam, userPhone]);

  const onVerifyPhoneConfirm: Function = useCallback((
    verificationCode: string, phone: string, onSuccess?: Function,
  ): void => {
    if (!verificationCode || !phone || !actionHash || !userId) {
      return;
    }

    dispatch(verifyUserPhoneSetup({
      verificationForm: {
        code: verificationCode,
        phone,
        actionHash,
      },
      userId,
    }, (): void => {
      closeVerifyPhoneModal();

      setStatusMessage('Verified successfully');
      setIsOpenMessage(true);

      if (onSuccess) {
        onSuccess();
      }
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setVerifyPhoneModalErrorMessage(exception.message);
      }
    }));
  }, [actionHash, userId]);

  const closeVerifyPhoneModal: Function = useCallback((): void => {
    setIsVerifyPhoneModalOpen(false);
    setVerifyPhoneModalErrorMessage(null);
  }, []);

  const onVerifyOtpSecretConfirm: Function = useCallback((verificationCode: string, onSuccess: Function): void => {
    if (!verificationCode || !userId || !actionHashParam) {
      return;
    }

    dispatch(verifyUserOtpSetup({
      verificationForm: {
        code: verificationCode,
      },
      actionHash: actionHashParam,
      userId,
    }, (): void => {
      setStatusMessage('Verified successfully');
      setIsOpenMessage(true);
      setConnectOtpModalErrorMessage(null);

      closeConnectOtpModal();

      if (onSuccess) {
        onSuccess();
      }
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setConnectOtpModalErrorMessage(exception.message);
      }
    }));
  }, [userId, actionHashParam]);

  const closeConnectOtpModal: Function = useCallback((): void => {
    setIsConnectOtpModalOpen(false);
  }, []);

  useEffect((): void => {
    if (actionHashParam) {
      dispatch(getUserVerificationSetupData({
        actionHash: actionHashParam,
      }, (): void => {
        setErrorMessage(null);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setErrorMessage(exception.message);
        }
      }));
    }
  }, [actionHashParam]);

  useEffect((): void => {
    if (userVerificationSetupData && userVerificationSetupData.user && !userPhone) {
      setUserPhone(userVerificationSetupData.user.phone || null);
    }
  }, [userVerificationSetupData, userIsPhoneVerified]);

  useEffect((): void => {
    if (userVerificationSetupData && (isUserPhoneVerified === null || userIsPhoneVerified !== isUserPhoneVerified)) {
      setIsUserPhoneVerified(userIsPhoneVerified);
    }
  }, [userVerificationSetupData]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  if (errorMessage) {
    return (
      <PageError
        isCentered
        message={errorMessage}
        extraComponent={(
          <TextBlock>
            Try to
            {' '}
            <FilteredLink link={ROUTE_LOGIN}>
              Sign In
            </FilteredLink>
            {' '}
            again.
          </TextBlock>
        )}
      />
    );
  }

  if (currentUser && currentUser.id) {
    return <Navigate to={homepageRoute || ROUTE_DASHBOARD} />;
  }

  if (!actionHashParam) {
    return <Navigate to={ROUTE_NOT_FOUND} />;
  }

  if (!isDataLoaded) {
    return <Loader isPageLoader />;
  }

  return (
    <Fragment>
      <Form withAutocomplete id={FORM_ID_VERIFICATION_SETUP} onChange={onFormChange}>
        <FlexContainer isCentered>
          <PaddedFlexContainer isLarge isColumn isTextCenter width={900}>
            <Grid withLargeSpacing>
              <GridItem isFullWidth>
                <TextHeader>
                  Protect your account
                </TextHeader>
                <TextBlock>
                  Your administrator requires you to enable MFA method for security reasons.
                </TextBlock>
                <LineBreak />
                <TextBlock>
                  Please fill out the form below or contact your administrator for more information.
                </TextBlock>
              </GridItem>
            </Grid>
            {
              actionHashParam && userVerificationSetupData && userVerificationSetupData.user && (
                <PaddedFlexContainer isLarge isColumn isTextLeft width={900}>
                  <Grid withLargeSpacing>
                    <GridItem isFullWidth={isMobileScreen} isMediumWidth={!isMobileScreen}>
                      <FormFieldSelect
                        isOutlined
                        withBlankElement
                        isDisabled={verificationTypeList && !verificationTypeList.length}
                        label="Default MFA Method"
                        name="verificationTypeDefault"
                        optionsTotal={verificationTypeList && verificationTypeList.length}
                        blankElementLabel="Auto"
                      >
                        {menuItemVerificationTypeList}
                      </FormFieldSelect>
                    </GridItem>
                    <GridItem isFullWidth={isMobileScreen} isMediumWidth={!isMobileScreen}>
                      <FormFieldPhone
                        isOutlined
                        name="phone"
                        label="Phone"
                        onChange={phoneChange}
                      />
                    </GridItem>
                    {
                      !isMobileScreen && (
                        <GridItem isMediumWidth />
                      )
                    }
                    <GridItem isFullWidth>
                      <Table isFullWidth>
                        <TableHead isSubHeader>
                          <TableRow>
                            <TableCell colSpan={COLUMNS_TOTAL}>
                              Available MFA Methods
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody withBorderedRows>
                          <TableRow>
                            <TableCell>
                              Phone Number SMS
                            </TableCell>
                            <TableCell>
                              <FlexContainer>
                                <ActionBlock
                                  withMultilineLabels
                                  isSuccessful={isUserPhoneVerified}
                                  labelSuccess="Enabled"
                                  labelError={`Not Enabled. Verification by phone can be enabled, `
                                    + `if phone number is verified only`}
                                  labelButton={!isUserPhoneVerified && !!userPhone && 'Verify'}
                                  onButtonClick={verifyPhone}
                                />
                              </FlexContainer>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              Authenticator App OTP
                            </TableCell>
                            <TableCell>
                              <FlexContainer withNoAlign isColumn={isSmallScreen}>
                                <ActionBlock
                                  withMultilineLabels
                                  isColumnView={isMobileScreen}
                                  isSuccessful={userIsOtpEnabled && userIsOtpVerified}
                                  labelSuccess="Enabled"
                                  labelError={(!userIsOtpEnabled && 'Not Enabled')
                                    || (userIsOtpEnabled && !userIsOtpVerified && 'Enabled, not verified')}
                                  labelButton={(!userIsOtpEnabled && 'Enable')
                                    || (userIsOtpEnabled && !userIsOtpVerified && 'Verify')}
                                  onButtonClick={connectOtpButtonClick}
                                />
                                {
                                  userIsOtpEnabled && (
                                    <FlexContainer>
                                      <Button
                                        isText
                                        isActive
                                        isDefault
                                        isExtended={!isMobileScreen}
                                        onClick={deleteOtpSecretButtonClick}
                                      >
                                        Delete Authenticator Data
                                      </Button>
                                    </FlexContainer>
                                  )
                                }
                              </FlexContainer>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </GridItem>
                  </Grid>
                </PaddedFlexContainer>
              )
            }
            <Grid withLargeSpacing>
              <GridItem isFullWidth>
                <Button
                  isActive
                  isDefault
                  isExtended
                  isDisabled={isSaveDisabled}
                  onClick={onSubmitForm}
                >
                  Enable and Save
                </Button>
              </GridItem>
            </Grid>
            {
              (!!formError || !!mainServerFormError) && !isFormSaving && (
                <ErrorBlock errorMessage={formError || mainServerFormError} />
              )
            }
          </PaddedFlexContainer>
        </FlexContainer>
      </Form>
      <VerifyPhoneModal
        isOpen={isVerifyPhoneModalOpen}
        phone={userPhone}
        errorMessage={verifyPhoneModalErrorMessage}
        onVerifyPhoneConfirm={onVerifyPhoneConfirm}
        onCloseModal={closeVerifyPhoneModal}
      />
      <ConnectOtpModal
        isOpen={isConnectOtpModalOpen}
        otpSecretHash={otpSecretHash}
        otpSecretQrCodeLink={otpSecretQrCodeLink}
        onVerifyOtpSecretConfirm={onVerifyOtpSecretConfirm}
        onCloseModal={closeConnectOtpModal}
        errorMessage={connectOtpModalErrorMessage}
      />
      <ConfirmModal
        isOpen={isDeleteOtpSecretConfirmModalOpen}
        title={deleteOtpSecretConfirmModalTitle}
        text={deleteOtpSecretConfirmModalText}
        onConfirm={deleteOtpSecretConfirm}
        onCloseModal={closeDeleteOtpSecretConfirmModal}
      />
      <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
    </Fragment>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    verificationSetup,
  } = reducers;

  initialValues.phone = (verificationSetup.userVerificationSetupData && verificationSetup.userVerificationSetupData.user
    && verificationSetup.userVerificationSetupData.user.phone) || null;
  initialValues.verificationTypeDefault = (verificationSetup.userVerificationSetupData
    && verificationSetup.userVerificationSetupData.user
    && verificationSetup.userVerificationSetupData.user.verificationTypeDefault) || null;

  return {
    isDataLoaded: !!verificationSetup.userVerificationSetupData,
    currentUser: app.baseData && app.baseData.currentUser,
    userVerificationSetupData: verificationSetup.userVerificationSetupData,
    otpSecretQrCodeLink: verificationSetup.otpSecretQrCodeLink,
    otpSecretHash: verificationSetup.otpSecretHash,
    actionHash: verificationSetup.actionHash,
    homepageRoute: verificationSetup.homepageRoute,
  };
};

const VerificationSetupForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ verificationSetup }: Reducers): ValidationErrorsMap => (
    verificationSetup.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ passwordRecovery }: Reducers): ValidationErrorsMap => (
    passwordRecovery.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_VERIFICATION_SETUP}
      component={VerificationSetup}
      validator={VerificationSetupValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(VerificationSetupForm);
